/**
 * DeliveriesButton — „z której dostawy zszedł ten towar" przy ilości na pozycji magazynowej.
 *
 * Dane są JUŻ w dokumencie: Symfonia dokleja listę dostaw do każdej pozycji, więc otwarcie okna
 * nie wywołuje żadnego zapytania. Dlatego to modal, a nie leniwie dociągany panel.
 *
 * Modal, nie dymek pod kursorem: tabela pozycji siedzi w kontenerze ze scrollem, w którym
 * pozycjonowany „na sztywno" popover bywa przycięty (konwencja repo §4.12). Treść jest malutka,
 * więc modal to też najtańsza poprawna odpowiedź.
 *
 * DWA STANY BEZ IKONY, obydwa normalne, nie awarie:
 *   • dokument w BUFORZE — Symfonia przypisuje partie dopiero przy zatwierdzeniu,
 *   • pozycja usługowa — usługa nie schodzi z magazynu.
 * W buforze pokazujemy wyszarzoną ikonę z wyjaśnieniem (użytkownik ma wiedzieć CZEMU nie ma
 * danych), przy usłudze nie ma nic — pytanie „z której dostawy" po prostu nie ma tam sensu.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HmWarehousePosition } from '@saas/shared';
import { isAxiosError } from 'axios';
import { ExternalLink, Layers } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

function day(value: string | null): string {
  return value ? value.slice(0, 10) : '—';
}

/**
 * Czy kod dostawy wygląda na numer dokumentu, a nie na kod partii.
 *
 * Symfonia trzyma w jednym polu obie rzeczy: numer przyjęcia („0011/26/PZ") i kod partii nadany
 * przy przyjęciu („059056686400648621"). Ukośnik rozdziela je wystarczająco dobrze, żeby nie
 * pokazywać odsyłacza tam, gdzie i tak nie ma dokąd pójść. To HEURYSTYKA, nie gwarancja —
 * dlatego nieudane wyszukanie jest obsłużone komunikatem, a nie traktowane jako błąd.
 */
function looksLikeDocumentNumber(code: string): boolean {
  return code.includes('/');
}

export function DeliveriesButton({
  position,
  inBuffer,
}: {
  position: HmWarehousePosition;
  inBuffer: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const companyId = useActiveCompanyScope().companyId;
  const [open, setOpen] = useState(false);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const deliveries = position.deliveries;

  /**
   * Dostawa niesie NUMER dokumentu, a podgląd potrzebuje identyfikatora — stąd wyszukanie po
   * numerze przed przejściem. „Nie znaleziono" jest spodziewane (kod partii, dokument usunięty),
   * więc kończy się komunikatem, nie ekranem błędu.
   */
  async function openSourceDocument(code: string) {
    setPendingCode(code);
    try {
      const res = await api.get<{ id: number }>(
        `/handel-magazyn/warehouse-documents/by-number?number=${encodeURIComponent(code)}&companyId=${companyId}`,
      );
      setOpen(false);
      navigate(`/handel-magazyn/magazyn/${res.data.id}`);
    } catch (err) {
      const code404 =
        isAxiosError(err) && (err.response?.data as { error?: string } | undefined)?.error;
      toast.show({
        variant: 'warning',
        title:
          code404 === 'DOCUMENT_NOT_FOUND'
            ? t('tenant.handelMagazyn.warehouse.deliveries.notFound', { code })
            : t('tenant.handelMagazyn.problem.UNKNOWN'),
      });
    } finally {
      setPendingCode(null);
    }
  }

  if (deliveries.length === 0) {
    if (!inBuffer) return null;
    return (
      <span
        className="inline-flex text-text-tertiary"
        title={t('tenant.handelMagazyn.warehouse.deliveries.bufferHint')}
        data-testid="hm-deliveries-buffer"
      >
        <Layers className="h-3.5 w-3.5 opacity-40" aria-hidden />
      </span>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        title={t('tenant.handelMagazyn.warehouse.deliveries.open')}
        aria-label={t('tenant.handelMagazyn.warehouse.deliveries.open')}
        className="h-6 w-6 p-0"
        data-testid={`hm-deliveries-${position.id}`}
      >
        <Layers className="h-3.5 w-3.5" aria-hidden />
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('tenant.handelMagazyn.warehouse.deliveries.title')}
        description={`${position.productCode} — ${position.description}`}
        size="md"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-primary text-left text-xs uppercase tracking-wide text-text-tertiary">
              <th className="px-2 py-1.5 font-medium">
                {t('tenant.handelMagazyn.warehouse.deliveries.code')}
              </th>
              <th className="px-2 py-1.5 font-medium">
                {t('tenant.handelMagazyn.warehouse.deliveries.date')}
              </th>
              <th className="px-2 py-1.5 text-right font-medium">
                {t('tenant.handelMagazyn.detail.pos.quantity')}
              </th>
            </tr>
          </thead>
          <tbody>
            {deliveries.map((d) => (
              <tr key={d.id} className="border-b border-border-primary/60">
                <td className="px-2 py-1.5">
                  {looksLikeDocumentNumber(d.code) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={pendingCode === d.code}
                      onClick={() => void openSourceDocument(d.code)}
                      title={t('tenant.handelMagazyn.warehouse.deliveries.goToSource', {
                        code: d.code,
                      })}
                      className="h-auto px-1 py-0.5 font-mono text-xs text-accent-600 hover:underline"
                    >
                      {d.code}
                      <ExternalLink className="h-3 w-3" aria-hidden />
                    </Button>
                  ) : (
                    <span className="font-mono text-xs text-text-primary">{d.code}</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-text-secondary">{day(d.date)}</td>
                <td className="px-2 py-1.5 text-right font-medium text-text-primary">
                  {d.quantity} {position.unit}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Rozchód idzie po kolejce partii, więc jedna pozycja potrafi zejść z kilku dostaw —
            wtedy warto powiedzieć wprost, że to podział jednej ilości, a nie osobne wydania. */}
        {deliveries.length > 1 && (
          <p className="mt-3 text-xs text-text-secondary">
            {t('tenant.handelMagazyn.warehouse.deliveries.split', {
              count: deliveries.length,
              total: `${position.quantity} ${position.unit}`,
            })}
          </p>
        )}
      </Modal>
    </>
  );
}
